<template>
  <div class="grid gap-24">
    <p class="text-[--raw-verydarkgrey]">
      {{ i18n(translations.intro) }}
    </p>

    <!-- Preflight strips list markers; the numbers come back from the style block. -->
    <ol data-steps class="text-[--raw-verydarkgrey]">
      <li><InlineMarkdown :message="translations.step1" /></li>
      <li>{{ i18n(translations.step2) }}</li>
    </ol>

    <!-- Nothing to submit, so Enter is swallowed rather than reloading the page. -->
    <form
      :aria-label="i18n(translations.formLabel)"
      class="grid gap-16 md:grid-cols-2"
      @submit.prevent
    >
      <label
        v-for="field in FIELDS"
        :key="field.name"
        data-field
        class="rounded-md relative block overflow-hidden border-[1px] border-[--raw-lightgrey] bg-[white]/70 transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] focus-within:bg-[white] hover:bg-[white]"
      >
        <!-- `placeholder=" "` is the `:placeholder-shown` hook, not copy. -->
        <input
          v-model="form[field.name]"
          type="text"
          placeholder=" "
          :autocomplete="field.autocomplete"
          class="rounded-md h-[58px] w-full bg-transparent px-16 pb-[10px] pt-[28px] text-[--raw-black] outline-offset-[-2px]"
        />
        <span data-field-text data-field-label>
          {{ i18n(field.label) }}
        </span>
        <span data-field-text data-field-echo aria-hidden="true">
          {{ i18n(field.label) }}
        </span>
      </label>
    </form>

    <div class="grid justify-items-start gap-8">
      <button
        type="button"
        class="raw-cta-3 rounded-full flex h-36 w-fit cursor-pointer items-center gap-8 bg-[white] px-16 transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:bg-[color-mix(in_oklab,var(--raw-lightgrey)_75%,transparent)] active:bg-[color-mix(in_oklab,var(--raw-lightgrey)_75%,transparent)]"
        @click="handleCopy()"
      >
        {{ i18n(isCopied ? translations.copiedCta : translations.copyCta) }}
      </button>

      <p v-if="hasCopyError" class="raw-body-3 text-[--raw-red]">
        {{ i18n(translations.copyError) }}
      </p>
    </div>

    <div
      class="rounded-md grid gap-16 bg-[white]/40 p-24 text-[--raw-verydarkgrey]"
    >
      <template v-for="(block, index) in blocks" :key="index">
        <ul v-if="block.list" data-bullets>
          <li v-for="line in block.lines" :key="line">{{ line }}</li>
        </ul>

        <p v-else class="whitespace-pre-line">{{ block.text }}</p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import InlineMarkdown from '~/scopes/campaign/components/UI/InlineMarkdown.vue'
import type { I18nDescriptor } from '~/scopes/campaign/data/metals/types'

import translations from './MpLetter.translations'

// The UK card's body; the chrome is `TakeActionModal`'s, as with the footer documents.
const i18n = useI18n()
const { trackClick } = useTracking()

const form = reactive({
  mpName: '',
  constituency: '',
  yourName: '',
  postcode: '',
})

const FIELDS = [
  { name: 'mpName', label: translations.mpNameLabel, autocomplete: 'off' },
  {
    name: 'constituency',
    label: translations.constituencyLabel,
    autocomplete: 'off',
  },
  {
    name: 'yourName',
    label: translations.yourNameLabel,
    autocomplete: 'name',
  },
  {
    name: 'postcode',
    label: translations.postcodeLabel,
    autocomplete: 'postal-code',
  },
] as const

const BULLET = '• '

// One entry per paragraph: blank line between blocks, hard break between lines.
const LETTER: { lines: I18nDescriptor[]; list?: true }[] = [
  { lines: [translations.letterGreeting] },
  { lines: [translations.letterConstituent] },
  { lines: [translations.letterPartnership] },
  {
    lines: [translations.letterDebateDetails, translations.letterCampaignAim],
  },
  {
    lines: [
      translations.letterAffordability,
      translations.letterResources,
      translations.letterHumanCost,
    ],
  },
  { lines: [translations.letterRefurbished] },
  { lines: [translations.letterManufacturing] },
  { lines: [translations.letterUrgeToSpeak] },
  { lines: [translations.letterConsiderFollowing] },
  {
    list: true,
    lines: [
      translations.letterQuestion1,
      translations.letterQuestion2,
      translations.letterQuestion3,
      translations.letterQuestion4,
    ],
  },
  { lines: [translations.letterParliamentaryQuestions] },
  { lines: [translations.letterSignOff] },
  { lines: [translations.letterYourName] },
  { lines: [translations.letterYourPostcode] },
]

// An empty field falls back to the client's own `[INSERT …]` token, so a letter copied
// without filling the form is still the letter they wrote.
const values = computed(() => ({
  mpName: form.mpName.trim() || i18n(translations.mpNamePlaceholder),
  constituency:
    form.constituency.trim() || i18n(translations.constituencyPlaceholder),
  yourName: form.yourName.trim() || i18n(translations.yourNamePlaceholder),
  postcode: form.postcode.trim() || i18n(translations.postcodePlaceholder),
}))

// Screen and clipboard read the same strings, so the two can't drift — `text` carries the
// bullets a list draws in CSS.
const blocks = computed(() =>
  LETTER.map(({ lines, list }) => {
    const rendered = lines.map((line) => i18n(line, values.value))
    const prefix = list ? BULLET : ''

    return {
      list,
      lines: rendered,
      text: rendered.map((line) => prefix + line).join('\n'),
    }
  }),
)

const isCopied = ref(false)
const hasCopyError = ref(false)
let copiedTimeout: ReturnType<typeof setTimeout> | undefined

// A failure has to show, unlike `UI/ShareButton`: `navigator.clipboard` needs a secure
// context, and this button is the whole point of the card.
async function handleCopy() {
  trackClick({ zone: 'mp-letter', name: 'copy-letter' })

  try {
    await navigator.clipboard.writeText(
      blocks.value.map((block) => block.text).join('\n\n'),
    )
    hasCopyError.value = false
    isCopied.value = true
    clearTimeout(copiedTimeout)
    copiedTimeout = setTimeout(() => {
      isCopied.value = false
    }, 1000)
  } catch {
    hasCopyError.value = true
  }
}
</script>

<style scoped>
/* The marker recipe from `footer/docs/MethodBlocks.vue`. */
[data-steps],
[data-bullets] {
  display: grid;
  gap: 0.5rem;
  padding-inline-start: 1.25rem;
  list-style: decimal;
}

[data-bullets] {
  list-style: disc;
}

/* A native placeholder cannot be animated, hence our own span for it. */
[data-field-text] {
  position: absolute;
  inset-inline-start: 16px;
  top: 50%;
  transform: translateY(calc(-50% + var(--shift))) scale(0.75);
  transform-origin: left center;
  color: var(--raw-darkgrey);
  pointer-events: none;
  transition:
    transform var(--raw-out-quart--fast),
    opacity var(--raw-out-quart--fast);
}

/* Equal and opposite: the two halves mirror each other out of the centre they share. */
[data-field-label] {
  --shift: -12px;
}

[data-field-echo] {
  --shift: 12px;
  opacity: 0;
}

/* At rest both sit dead centre at full size, and only the echo is painted — two copies of
   one string on the same pixel would read as bolder. */
[data-field] input:placeholder-shown:not(:focus) ~ [data-field-text] {
  transform: translateY(-50%) scale(1);
}

[data-field] input:placeholder-shown:not(:focus) ~ [data-field-label] {
  opacity: 0;
}

[data-field] input:placeholder-shown:not(:focus) ~ [data-field-echo] {
  opacity: 1;
}

/* `assets/css/buttons.css` does this for `button` and `a`, but predates the scope's inputs. */
[data-field] input:focus-visible {
  outline-color: var(--raw-verydarkgrey);
}

/* Reduced motion keeps the crossfade and drops the travel. */
@media (prefers-reduced-motion: reduce) {
  [data-field-text] {
    transition: opacity var(--raw-out-quart--fast);
  }
}
</style>
